import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Provider, useSetAtom } from 'jotai';
import { SummaryContainer } from '@components/summary-panel';
import { MetricsChartContainer as HzMetricsChartContainer } from '@components/metrics-panel-hz';
import { ErrorBoundary, PageTitle, Footer, TimePanel, Retry } from '@components/common';
import RetryContext from '@src/contexts/retryContext';
import DimensionContainer from '@components/dimension-panel-hz';
import MetricFilterBar from '@components/summary-panel/MetricFilterBar';
import { buildDashboardStore } from '@src/atoms/utils';
import CompareFiltersBar from '@components/summary-panel/CompareFiltersBar';
import { EMPWidgetContainer } from '@components/common/EmpWidget';
import { ContentWrapper, HzMetricsPanelWrapper } from '../main/styled';
import { get } from 'lodash';
import { updateInterval, applyFilter } from '@store/common/actions';
import { setMetrics, selectedMetricChart } from '@store/metricsChartContainer/actions';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { resetDrillDown } from '@store/summaryContainer/drilldown/actions';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { sizeAtom } from '@components/metrics-panel-hz/atoms';
import { useEcoServerAIAlerts } from '@src/hooks/useEcoServerAIAlerts';
import { ZERO } from '@src/utils/constants';

const defaultStore = buildDashboardStore();

const ContextTrends = ({ props, dashboard }) => {
  const dispatch = useDispatch();
  const setMetricChartSize = useSetAtom(sizeAtom);
  const dashboardInterval = get(dashboard, 'timeInterval');
  const filterList = get(dashboard, 'filterList');
  const metricList = get(dashboard, 'metricList');
  const alertId = get(dashboard, 'alertId');

  const intervalArray = dashboardInterval.split('/');
  const interval = {
    from: intervalArray[0],
    to: intervalArray[1],
    shortcut: 'custom',
  };

  const crumbRules = getRulesFromFilter(filterList, true);
  const crumbFilter = getFilterFromRules(crumbRules, true);

  const filterToApply = {
    name: crumbFilter.name,
    rule: filterList,
  };

  const { alertData, isFetching, isError, refetch } = useEcoServerAIAlerts(alertId, filterToApply);

  useEffect(() => {
    dispatch(resetDrillDown([]));
    dispatch(pushDrilldownFilter(filterToApply.name, filterToApply.rule));
    dispatch(applyFilter(filterToApply));
    // Update interval only if it is not set as part of the alert data
    if (!alertData) {
      dispatch(updateInterval(interval.from, interval.to, 'custom', 'trends'));
    }
    // Update the metriclist for an alert. Default dashboards will have empty metric list
    if (metricList.length > ZERO) {
      dispatch(selectedMetricChart(metricList[ZERO], null));
      dispatch(setMetrics(metricList));
    }
    setMetricChartSize('l');
    return () => {};
  }, [filterList, alertData]);

  return (
    <RetryContext.Provider value={{ interval }}>
      <ErrorBoundary>
        <PageTitle text="Trends" />
        <TimePanel />
        <SummaryContainer
          isSharing={false}
          id={1}
          liveModeSelected={false}
          isSavedDashboard={false}
          pageKeyForSaveService={''}
          savingState={{}}
          savedDashboardCreatorId={''}
        />
        <CompareFiltersBar />
        <ContentWrapper layout={true}>
          <Retry isLoading={isFetching} showError={isError} fetchData={refetch}>
            <MetricFilterBar />
            <HzMetricsPanelWrapper>
              <HzMetricsChartContainer />
            </HzMetricsPanelWrapper>
          </Retry>
          <DimensionContainer />
        </ContentWrapper>
        <Footer />
        <EMPWidgetContainer />
      </ErrorBoundary>
    </RetryContext.Provider>
  );
};

const WrappedContextTrends = (props) => {
  const { store = defaultStore }: any = props;
  const { match } = props;
  const { context, alertId } = match.params;
  const localStorageKey = `eco-server-alert-${alertId}`;
  const alertItem = JSON.parse(localStorage.getItem(localStorageKey) || '');
  const dashboard = alertItem[context];
  // Adding alert id to dashboard information in order to fetch the alert data
  dashboard['alertId'] = alertId;

  return (
    <Provider store={store}>
      <ContextTrends {...props} dashboard={dashboard} />
    </Provider>
  );
};
WrappedContextTrends.defaultProps = {
  isSharing: false,
  match: {},
  collapseStatus: {},
};
export default WrappedContextTrends;
